import { computed } from 'vue'
import has from 'lodash/has'
import EventBus from '@/components/mixins/event-bus-mixin'
import AuthMixin from '@/components/mixins/auth-mixin'
import FormValidationMixin from '@/components/mixins/form-validation-mixin'
import ResetPassword from '@/components/common/sign-in/forgot-password-reset-password.vue'
import { updateUserAttributes as AmplifyUpdateUserAttributes } from 'aws-amplify/auth'
import { useScreenSize } from '@base-shared/composables/use-screen-size';
import { useMixpanel } from '@base-shared/composables/use-mixpanel';
import { useUserStore } from '@base-shared/stores/user';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

export default {
  name: 'userProfile',
  inject: [
    '$utils',
    '$api',
    '$apiError',
    '$signedInUserDetails'
  ],
  components: {
    ResetPassword
  },
  props: {},
  watch: {
    /**
     * Watches the `userStore.currentUser` for changes. Replacement
     * for the old `BIND_USER_DETAILS_IN_VUE` from EventBus.
     */
    'userStore.currentUser': {
      handler (newValue) {
        this.setDefaultData()
      },
      deep: true
    }
  },
  mixins: [AuthMixin, FormValidationMixin],

  setup () {
    const { isPageMini } = useScreenSize();
    const cognitoAuthStore = useCognitoAuthStore();

    const isChangeEmailPasswordAllowed = computed(() => {
      const session = cognitoAuthStore.authSession;
      return session && !has(session?.tokens?.idToken.payload, 'at_hash');
    });

    // return to expose to template or Options API methods
    return {
      isPageMini,
      isChangeEmailPasswordAllowed,
      userStore: useUserStore()
    };
  },
  data () {
    return {
      error: '',
      userProfileFormDefault: {},
      userProfileForm: {},
      userProfileFormOnSubmitStatus: {
        isNameDataRequestDone: false,
        isEmailDataRequestDone: false
      },
      userProfileRules: {
        firstName: [
          { required: true, message: this.$t('label.requiredFirstName') }
        ],
        lastName: [
          { required: true, message: this.$t('label.requiredLastName') }
        ],
        email: [
          { required: true, message: this.$t('label.requiredEmail') }
        ],
        password: [
          { required: true, message: this.$t('label.requiredStreetAndNumber') }
        ]
      },
      showChangePasswordModal: false,
      showPasswordChangeSuccess: false,
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action) {
        this.$emit('user-profile-change', { action, payload: { data } })
      }
    },
    setDefaultData () {
      this.userProfileFormDefault = this.$utils.clone(this.$signedInUserDetails)
      this.userProfileForm = this.$utils.clone(this.$signedInUserDetails)
    },
    handleActionsAfterUserProfileFormSubmit () {
      if (this.userProfileFormOnSubmitStatus.isNameDataRequestDone || this.userProfileFormOnSubmitStatus.isEmailDataRequestDone) {
        // Update user details
        this.getUserDetailsFromBackend({
          callback: () => {
            this.$utils.mainLoader(false)
            EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
              description: this.$t('label.updatedProfile'),
              closeButtonText: this.$t('buttons.ok'),
              callbacks: {
                eventOnClose: () => {
                  this.userProfileFormOnSubmitStatus = {
                    isNameDataRequestDone: false,
                    isEmailDataRequestDone: false
                  }
                }
              }
            })
          }
        })
      }
    },
    handleUserProfileFormSubmit () {
      const formEl = $(this.$refs.userProfileForm.$el)
      formEl.find('.el-form-item').removeClass('animated shake')

      // Reset at the start of form submit
      this.userProfileFormOnSubmitStatus.isNameDataRequestDone = false
      this.userProfileFormOnSubmitStatus.isEmailDataRequestDone = false

      this.$refs.userProfileForm.validate(async (isFormValid) => {
        if (isFormValid) {
          // Update FirstName and LastName in Cognito User attributes if value changed
          const { firstName, lastName } = this.userProfileForm
          const { firstName: defaultFirstName, lastName: defaultLastName } = this.userProfileFormDefault

          if (firstName !== defaultFirstName || lastName !== defaultLastName) {
            this.$utils.mainLoader(true)

            try {
              await AmplifyUpdateUserAttributes({
                userAttributes: {
                  given_name: firstName,
                  family_name: lastName
                }
              })
              this.userProfileFormOnSubmitStatus.isNameDataRequestDone = true
            } catch (err) {
              const errorMessage = err?.message || JSON.stringify(err)
              EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
                type: 'error',
                alert: {
                  message: errorMessage
                }
              })

              return;
            }
          }

          // Update Email in the backend if value changed
          const oldEmail = this.userProfileFormDefault?.email ?? ''
          const newEmail = this.userProfileForm?.email ?? ''

          // Only allow to change email if user is not SSO/SSI user
          if (newEmail && newEmail !== oldEmail && this.isChangeEmailPasswordAllowed) {
            const changeEmailRequest = async () => {
              const requestData = { email: newEmail.toLowerCase() }

              this.$utils.mainLoader(true)
              try {
                await this.$api.put(`${CONFIG.API_BASE_URL}/group/users/me`, requestData)

                this.userProfileFormOnSubmitStatus.isEmailDataRequestDone = true
                return { success: true }
              } catch (error) {
                this.$utils.mainLoader(false)
                // Change error message if email already exist
                if (error?.response?.data?.name === 'EmailExistsError') {
                  error.response.data.description = this.$t('label.emailAlreadyExists')
                }
                this.$apiError.handleAPIRequestError(error, { originalRequest: () => { changeEmailRequest() }, showDialogNotification: true })
                return { success: false }
              }
            }
            const { success } = await changeEmailRequest()

            // If email change failed, revert the email field to the old value and return early to not show success pop up
            if (!success) {
              this.userProfileForm.email = oldEmail
              return;
            }
          }

          this.handleActionsAfterUserProfileFormSubmit()
        } else {
          // Animate error fields
          formEl.find('.el-form-item').each((indx, el) => {
            if (($(el).hasClass('is-error'))) {
              window.setTimeout(() => { this.$utils.animate(el, 'animated shake') }, 1)
            }
          })
        }
      })
    },
    handleDeleteUser () {
      // Send global event to show custom delete confirmation dialog
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: this.$t('confirm.deleteUser'),
        descriptionI18n: {
          key: 'userProfile.deleteUserConfirmation',
          params: {
            permanent: this.$t('userProfile.permanent')
          }
        },
        alert: {
          message: this.$t('userProfile.deleteUserWarning', { lose: this.$t('userProfile.lose') })
        },
        alertMessageI18n: {
          key: 'userProfile.deleteUserWarning',
          params: {
            lose: {
              type: 'strong',
              text: this.$t('userProfile.lose')
            }
          }
        },
        confirmButtonText: this.$t('buttons.deleteUser'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOnClose: () => {},
          eventOKButtonClick: () => {
            const deleteAccountRequest = () => {
              this.$utils.mainLoader(true)
              this.$api.delete(`${CONFIG.API_BASE_URL}/user/me`)
                .then(response => {
                  this.$utils.mainLoader(false)
                  this.signOut()
                })
                .catch((error) => {
                  this.$utils.mainLoader(false)
                  let dialogErrorMessage = error.response.data?.message ?? ''
                  if (dialogErrorMessage.includes('cannot delete user as you are the only user in the account')) {
                    dialogErrorMessage = this.$t('label.youCannotDeleteUserAsOnlyUserInAccount')
                  }

                  this.$apiError.handleAPIRequestError(error, {
                    originalRequest: () => { deleteAccountRequest() },
                    showDialogNotification: true,
                    showCommonGoBackButton: true,
                    dialogErrorMessage
                  })
                })
            }
            deleteAccountRequest()
          }
        }
      })
    },
    handlePasswordComponentChanged ($event) {
      this.showChangePasswordModal = false
    },
    toggleChangePasswordModal () {
      this.showChangePasswordModal = !this.showChangePasswordModal;
      this.showPasswordChangeSuccess = false;
    },
  },
  mounted () {
    useMixpanel().track('User Profile')
    this.isAuthenticated({
      callback: () => {
        this.setDefaultData()
      }
    })
  },
}
